import { getAccessToken } from "../lib/auth";
import { parseApiError } from "../lib/api-error";
import type { OrgUnit, OrgUnitMember, MemberAppRoles } from "../types/org";

async function fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = await getAccessToken();
  const response = await fetch(`/api/org-units${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
  });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  return response.json();
}

export const orgUnitsApi = {
  tree: async (): Promise<OrgUnit[]> => {
    const res = await fetchAPI<{ tree: OrgUnit[] }>("/tree");
    return res.tree;
  },

  list: (params?: { type?: string; parentId?: string; search?: string }) => {
    const qs = new URLSearchParams();
    if (params?.type) qs.set("type", params.type);
    if (params?.parentId) qs.set("parentId", params.parentId);
    if (params?.search) qs.set("search", params.search);
    const q = qs.toString();
    return fetchAPI<{ items: OrgUnit[] }>(q ? `?${q}` : "");
  },

  get: (id: string) => fetchAPI<OrgUnit>(`/${id}`),

  create: (data: {
    name: string;
    slug: string;
    type: string;
    parentId?: string;
    keycloakOrgId?: string;
    metadata?: Record<string, unknown>;
  }) => fetchAPI<OrgUnit>("", { method: "POST", body: JSON.stringify(data) }),

  update: (id: string, data: Partial<{
    name: string;
    slug: string;
    type: string;
    status: string;
    parentId: string | null;
    keycloakOrgId: string | null;
    keycloakGroupId: string | null;
    keycloakGroupPath: string | null;
    maxMembers: number | null;
    metadata: Record<string, unknown>;
  }>) =>
    fetchAPI<{ ok: boolean }>(`/${id}`, { method: "PATCH", body: JSON.stringify(data) }),

  delete: (id: string) => fetchAPI<void>(`/${id}`, { method: "DELETE" }),

  getMembers: (id: string) => fetchAPI<{ members: OrgUnitMember[] }>(`/${id}/members`),

  addMember: (id: string, userId: string) =>
    fetchAPI<void>(`/${id}/members`, { method: "POST", body: JSON.stringify({ userId }) }),

  removeMember: (id: string, userId: string) =>
    fetchAPI<void>(`/${id}/members/${userId}`, { method: "DELETE" }),

  getMemberAppRoles: (unitId: string, userId: string, clientId: string) =>
    fetchAPI<MemberAppRoles>(`/${unitId}/members/${userId}/app-roles?clientId=${clientId}`),

  assignMemberAppRoles: (unitId: string, userId: string, data: { clientId: string; roles: string[] }) =>
    fetchAPI<{ ok: boolean; assigned: string[] }>(`/${unitId}/members/${userId}/app-roles`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  removeMemberAppRoles: (unitId: string, userId: string, data: { clientId: string; roles: string[] }) =>
    fetchAPI<{ ok: boolean; removed: string[] }>(`/${unitId}/members/${userId}/app-roles`, {
      method: "DELETE",
      body: JSON.stringify(data),
    }),

  syncFromKeycloak: () =>
    fetchAPI<{ created: number; items: Array<{ name: string; slug: string; type: string; path: string }> }>(
      "/sync-from-keycloak",
      { method: "POST" },
    ),

  ensureCompany: (data: { keycloakOrgId: string; name: string; slug: string }) =>
    fetchAPI<OrgUnit>("/ensure-company", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  listByOrg: (keycloakOrgId: string, type?: string) => {
    const params = new URLSearchParams({ keycloakOrgId });
    if (type) params.set("type", type);
    return fetchAPI<{ items: OrgUnit[] }>(`?${params}`);
  },

  batchMemberAppRoles: (unitId: string, clientIds: string[]) =>
    fetchAPI<{ roles: Record<string, Record<string, string[]>> }>(
      `/${unitId}/members/batch-app-roles?clientIds=${clientIds.join(",")}`
    ),
};
